/**
 * Static action registry — single source of truth for all domain actions.
 *
 * Each resource is defined with `resource(name, displayName)`, optionally
 * chained with `.childOf(parent)`, and completed with `.addAction({...})`.
 * Parents must be defined before their children.
 *
 * Adding a new action:
 *   1. Find the resource (or create one with `resource()`/`.childOf()`).
 *   2. Add the action key to the resource's `.addAction()` object literal:
 *        const myResource = resource('my-resource', 'My Resource')
 *          .childOf(parent)
 *          .addAction({
 *            DoThing: { description: '...', permission: true, audit: true },
 *            ClickThing: { description: '...', tracking: true, legacyTrackingName: 'Click Thing' },
 *          });
 *   3. The registry auto-generates typed exports:
 *      - Permission.MyResourceDoThing  ('my-resource:do-thing')
 *      - AuditEventType.MyResourceDoThing  (audit string)
 *      - TrackingEvent.MyResourceClickThing (legacy Mixpanel string)
 */

import {
  resource,
  buildRegistry,
  type ActionDef,
  type ActionEntry,
  type RegistryOutput,
} from './action-registry-builder.js';

// Re-export shared types for backward compat
export type { ActionDef, ActionEntry, RegistryOutput };

// ===== Resources =====

const root = resource('root', 'Root').addAction({});

const matter = resource('matter', 'Matters')
  .childOf(root)
  .addAction({
    AccessAll: {
      description: 'View all Matters in your organisation - not only assigned ones',
      permission: true,
    },
    ViewUsers: {
      description: 'View all users within a Matter',
      permission: true,
    },
    Create: {
      description: 'Create a new Matter, and edit or delete Matters you have created',
      permission: true,
      audit: true,
      tracking: true,
      legacyTrackingName: 'Create New Matter',
    },
    CreateSample: {
      description: 'Create a sample Matter',
      tracking: true,
      legacyTrackingName: 'Create Sample Matter',
    },
    Edit: {
      description: 'Edit any Matter you have access to',
      permission: true,
      audit: true,
      tracking: true,
      legacyTrackingName: 'Edit Matter',
    },
    Delete: {
      description: 'Delete any Matter you have access to',
      permission: true,
      audit: true,
    },
    AddUsers: {
      description: 'Add users to a Matter',
      permission: true,
      audit: true,
    },
    RemoveUsers: {
      description: 'Remove users from a Matter',
      permission: true,
      audit: true,
    },
    GenerateDetails: {
      description: 'Generate Matter details with AI',
      tracking: true,
      legacyTrackingName: 'Generate Matter Details',
    },
    AddTeamMemberOnCreator: {
      description: 'Add team member during Matter creation',
      tracking: true,
      legacyTrackingName: 'Add Team Member On Matter Creator',
    },
    RemoveTeamMemberOnCreator: {
      description: 'Remove team member during Matter creation',
      tracking: true,
      legacyTrackingName: 'Remove Team Member On Matter Creator',
    },
    AddUser: {
      description: 'Add user to a Matter',
      tracking: true,
      legacyTrackingName: 'Add User To Matter',
    },
    AddUserFromMatterPage: {
      description: 'Add user to Matter from Matter page',
      tracking: true,
      legacyTrackingName: 'Add User To Matter From Matter Page',
    },
    RemoveUser: {
      description: 'Remove user from a Matter',
      tracking: true,
      legacyTrackingName: 'Remove User From Matter',
    },
    CopyLink: {
      description: 'Copy Matter link',
      tracking: true,
      legacyTrackingName: 'Clicked Copy Matter Link',
    },
    RequestAccess: {
      description: 'Request access to a Matter',
      tracking: true,
      legacyTrackingName: 'Request Matter Access',
    },
    InviteUserNoAccount: {
      description: 'Invite user who has no account',
      tracking: true,
      legacyTrackingName: 'Invited User With No Account',
    },
  });

const document = resource('document', 'Documents')
  .childOf(matter)
  .addAction({
    ClickView: {
      description: 'Click to view document',
      tracking: true,
      legacyTrackingName: 'Click View Document On Documents Page',
    },
    Upload: {
      description: 'Upload documents to a Matter',
      permission: true,
      delegatedBy: [[matter, 'Create']],
      audit: true,
    },
    ClickAdd: {
      description: 'Click add documents button',
      tracking: true,
      legacyTrackingName: 'Click Add Documents On Documents Page',
    },
    Update: {
      description: 'Update document names, dates, and authors',
      permission: true,
    },
    UpdateName: {
      description: 'Update document name',
      tracking: true,
      legacyTrackingName: 'Update Document Name',
    },
    UpdateDate: {
      description: 'Update document date',
      tracking: true,
      legacyTrackingName: 'Update Document Date',
    },
    UpdateAuthor: {
      description: 'Update document author',
      tracking: true,
      legacyTrackingName: 'Update Document Author',
    },
    Delete: {
      description: 'Delete documents from a Matter',
      permission: true,
      audit: true,
    },
    Download: {
      description: 'Download documents and document lists',
      permission: true,
    },
    DownloadList: {
      description: 'Download document list',
      tracking: true,
      legacyTrackingName: 'Download Document List',
    },
    DownloadEmailTemplate: {
      description: 'Download email template',
      tracking: true,
      legacyTrackingName: 'Download Email Template',
    },
    DownloadOverview: {
      description: 'Download overview document',
      tracking: true,
      legacyTrackingName: 'Download Overview Document',
    },
    DownloadCaseIssues: {
      description: 'Download case issues sheet',
      tracking: true,
      legacyTrackingName: 'Download Case Issues Sheet',
    },
    DownloadPersonsCsv: {
      description: 'Download persons table as CSV',
      tracking: true,
      legacyTrackingName: 'Download Persons Table CSV',
    },
    DownloadPersonsDocx: {
      description: 'Download persons table as DOCX',
      tracking: true,
      legacyTrackingName: 'Download Persons Table Docx',
    },
    DownloadPersonsWritten: {
      description: 'Download persons written report',
      tracking: true,
      legacyTrackingName: 'Download Persons Written Docx',
    },
    ApplyFilters: {
      description: 'Apply filters on documents page',
      tracking: true,
      legacyTrackingName: 'Apply Filters On Documents Page',
    },
    ApplySort: {
      description: 'Apply sort on documents page',
      tracking: true,
      legacyTrackingName: 'Apply Sort On Documents Page',
    },
  });

const subset = resource('subset', 'Subsets')
  .childOf(matter)
  .addAction({
    View: {
      description: 'View Subsets',
      tracking: true,
      legacyTrackingName: 'View Subsets',
    },
    ViewFactsFromSummary: {
      description: 'View facts from Subset summary',
      tracking: true,
      legacyTrackingName: 'View Facts From Subset Summary',
    },
    ViewDocumentsFromSummary: {
      description: 'View documents from subset summary',
      tracking: true,
      legacyTrackingName: 'View Documents From Subset Summary',
    },
    ViewEntitiesFromSummary: {
      description: 'View entities from subset summary',
      tracking: true,
      legacyTrackingName: 'View Entities From Subset Summary',
    },
    ViewKeyDocumentsFromSummary: {
      description: 'View key documents from subset summary',
      tracking: true,
      legacyTrackingName: 'View Key Documents From Subset Summary',
    },
    Create: {
      description: 'Create a Subset, and edit or delete Subsets you have created',
      permission: true,
      audit: true,
      tracking: true,
      legacyTrackingName: 'Create Subset',
    },
    OpenCreateNew: {
      description: 'Open create new subset dialog',
      tracking: true,
      legacyTrackingName: 'Open Create New Subset',
    },
    Edit: {
      description: 'Edit any Subset you have access to',
      permission: true,
      audit: true,
      tracking: true,
      legacyTrackingName: 'Edit Subset',
    },
    EditFromSummary: {
      description: 'Edit existing subset from summary page',
      tracking: true,
      legacyTrackingName: 'Edit Existing Subset From Summary',
    },
    Delete: {
      description: 'Delete any Subset you have access to',
      permission: true,
      audit: true,
    },
    CopyMatterIssues: {
      description: 'Copy Matter issues to subset',
      tracking: true,
      legacyTrackingName: 'Copy Matter Issues To Subset',
    },
    AddFilter: {
      description: 'Add filter to subset',
      tracking: true,
      legacyTrackingName: 'Add Filter To Subset',
    },
    ShowSelectedDocuments: {
      description: 'Click show selected documents',
      tracking: true,
      legacyTrackingName: 'Click Show Selected Documents',
    },
    ResetSelection: {
      description: 'Click reset selection',
      tracking: true,
      legacyTrackingName: 'Click Reset Selection',
    },
    SelectFromListClicked: {
      description: 'Subset select from list clicked',
      tracking: true,
      legacyTrackingName: 'Subset Select From List Clicked',
    },
    SelectFromListFileSubmitted: {
      description: 'Subset select from list file submitted',
      tracking: true,
      legacyTrackingName: 'Subset Select From List File Submitted',
    },
  });

const fact = resource('fact', 'Facts')
  .childOf(subset)
  .addAction({
    ViewKeyFromSummary: {
      description: 'View key facts from summary',
      tracking: true,
      legacyTrackingName: 'View Key Facts From Summary',
    },
    Create: {
      description: 'Manually create a fact',
      permission: true,
      audit: true,
    },
    CreateFromFactsPage: {
      description: 'Create fact from the facts page',
      tracking: true,
      legacyTrackingName: 'Create Fact From Facts Page',
    },
    CreateFromDocViewer: {
      description: 'Create fact from document viewer',
      tracking: true,
      legacyTrackingName: 'Create Fact From Doc Viewer',
    },
    AddOnFactsPage: {
      description: 'Add fact on facts page',
      tracking: true,
      legacyTrackingName: 'Add Fact On Facts Page',
    },
    ConfirmCreateFromImage: {
      description: 'Confirm create fact from image selection',
      tracking: true,
      legacyTrackingName: 'Confirm Create Fact From Image Selection',
    },
    SelectCreateFromImage: {
      description: 'Select to create fact from image selection',
      tracking: true,
      legacyTrackingName: 'Select To Create Fact From Image Selection',
    },
    Update: {
      description: 'Update fact descriptions, relevance, dates, and classifications',
      permission: true,
      audit: true,
    },
    UpdateContent: {
      description: 'Update fact content',
      tracking: true,
      legacyTrackingName: 'Updated Fact Content',
    },
    UpdateRelevance: {
      description: 'Update fact relevance',
      tracking: true,
      legacyTrackingName: 'Updated Fact Relevance',
    },
    UpdateDate: {
      description: 'Update fact date',
      tracking: true,
      legacyTrackingName: 'Updated Fact Date',
    },
    BulkUpdate: {
      description: 'Bulk update facts',
      audit: true,
      tracking: true,
      legacyTrackingName: 'Bulk Updated Fact',
    },
    SetKey: {
      description: 'Set key for facts',
      tracking: true,
      legacyTrackingName: 'Set Key For Facts',
    },
    Delete: {
      description: 'Delete or archive a fact',
      permission: true,
      audit: true,
    },
    DeleteFact: {
      description: 'Delete a fact',
      tracking: true,
      legacyTrackingName: 'Delete Fact',
    },
    BulkDelete: {
      description: 'Bulk delete facts',
      audit: true,
      tracking: true,
      legacyTrackingName: 'Bulk Delete Facts',
    },
    CommentAdded: {
      description: 'Comment added to fact',
      tracking: true,
      legacyTrackingName: 'Comment Added To Fact',
    },
    CopyId: {
      description: 'Copy fact ID',
      tracking: true,
      legacyTrackingName: 'Copy Fact ID',
    },
    ToggleAllOn: {
      description: 'Toggle all facts on',
      tracking: true,
      legacyTrackingName: 'Toggle All Facts On',
    },
    ToggleAllOff: {
      description: 'Toggle all facts off',
      tracking: true,
      legacyTrackingName: 'Toggle All Facts Off',
    },
    Search: {
      description: 'Search facts',
      tracking: true,
      legacyTrackingName: 'Search Facts',
    },
    ToggleKey: {
      description: 'Toggle key facts filter',
      tracking: true,
      legacyTrackingName: 'Toggle Key Facts',
    },
    ToggleDisputed: {
      description: 'Toggle disputed facts filter',
      tracking: true,
      legacyTrackingName: 'Toggle Disputed Facts',
    },
    FilterByDate: {
      description: 'Filter facts by date',
      tracking: true,
      legacyTrackingName: 'Filter Facts By Date',
    },
    FilterByDocument: {
      description: 'Filter facts by document',
      tracking: true,
      legacyTrackingName: 'Filter Facts By Document',
    },
    FilterByRelevance: {
      description: 'Filter facts by relevance',
      tracking: true,
      legacyTrackingName: 'Filter Facts By Relevance',
    },
    FilterByClassification: {
      description: 'Filter facts by classification',
      tracking: true,
      legacyTrackingName: 'Filter Facts By Classification',
    },
    FilterByProcessingRun: {
      description: 'Filter facts by processing run',
      tracking: true,
      legacyTrackingName: 'Filter Facts By Processing Run',
    },
    FilterByDocumentType: {
      description: 'Filter facts by document type',
      tracking: true,
      legacyTrackingName: 'Filter Facts By Document Type',
    },
    FilterByTheme: {
      description: 'Filter facts by theme',
      tracking: true,
      legacyTrackingName: 'Filter Facts By Theme',
    },
    FilterByPerson: {
      description: 'Filter facts by person',
      tracking: true,
      legacyTrackingName: 'Filter Facts By Person',
    },
  });

const chronology = resource('chronology', 'Chronologies')
  .childOf(subset)
  .addAction({
    View: {
      description: 'View a chronology',
      tracking: true,
      legacyTrackingName: 'Click View Chronology',
    },
    Create: {
      description: 'Create a chronology, and edit or delete chronologies you have created',
      permission: true,
      audit: true,
    },
    CreateFromFactsPage: {
      description: 'Create chronology from facts page',
      tracking: true,
      legacyTrackingName: 'Create Chronology From Facts Page',
    },
    Edit: {
      description: 'Edit any chronology',
      permission: true,
      audit: true,
    },
    UpdateName: {
      description: 'Update chronology name',
      tracking: true,
      legacyTrackingName: 'Update Chronology Name',
    },
    UpdateFactDate: {
      description: 'Update fact date in chronology',
      tracking: true,
      legacyTrackingName: 'Update Chronology Fact Date',
    },
    AddFact: {
      description: 'Add a fact to a chronology',
      audit: true,
    },
    AddFactOnPage: {
      description: 'Add fact on chronology page',
      tracking: true,
      legacyTrackingName: 'Add Fact To Chronology On Chronology Page',
    },
    AddFactOnVisualPage: {
      description: 'Add fact from table on visual chronology page',
      tracking: true,
      legacyTrackingName: 'Clicked to Add Fact From Table On Visual Chronology Page',
    },
    AddFromFactsPage: {
      description: 'Add to chronology from facts page',
      tracking: true,
      legacyTrackingName: 'Add To Chronology From Facts Page',
    },
    VerifyFact: {
      description: 'Verify a fact in a chronology',
      audit: true,
    },
    VerifyFactTracking: {
      description: 'Verify a chronology fact',
      tracking: true,
      legacyTrackingName: 'Verify Chronology Fact',
    },
    UnverifyFact: {
      description: 'Unverify a chronology fact',
      tracking: true,
      legacyTrackingName: 'Unverify Chronology Fact',
    },
    Delete: {
      description: 'Delete any chronology',
      permission: true,
      audit: true,
      tracking: true,
      legacyTrackingName: 'Delete Chronology',
    },
    RemoveFact: {
      description: 'Remove a fact from chronology',
      audit: true,
      tracking: true,
      legacyTrackingName: 'Remove Fact From Chronology',
    },
    Download: {
      description: 'Download a chronology',
      permission: true,
    },
    DownloadCsv: {
      description: 'Download chronology table as CSV',
      tracking: true,
      legacyTrackingName: 'Download Chronology Table CSV',
    },
    DownloadDocx: {
      description: 'Download chronology table as DOCX',
      tracking: true,
      legacyTrackingName: 'Download Chronology Table Docx',
    },
    DownloadWrittenDocx: {
      description: 'Download chronology written as DOCX',
      tracking: true,
      legacyTrackingName: 'Download Chronology Written Docx',
    },
    DownloadWrittenCustom: {
      description: 'Download chronology with custom style',
      tracking: true,
      legacyTrackingName: 'Download Chronology Written Custom Style',
    },
    DownloadWithDocuments: {
      description: 'Download chronology with documents',
      tracking: true,
      legacyTrackingName: 'Download Chronology With Documents',
    },
    AddVisualNode: {
      description: 'Add visual chronology node',
      tracking: true,
      legacyTrackingName: 'Add Visual Chronology Node',
    },
    EditVisualNode: {
      description: 'Edit visual chronology node',
      tracking: true,
      legacyTrackingName: 'Edit Visual Chronology Node',
    },
    DeleteVisualNode: {
      description: 'Delete visual chronology node',
      tracking: true,
      legacyTrackingName: 'Delete Visual Chronology Node',
    },
    SaveVisual: {
      description: 'Save visual chronology',
      tracking: true,
      legacyTrackingName: 'Save Visual Chronology',
    },
    ExportVisual: {
      description: 'Export visual chronology',
      tracking: true,
      legacyTrackingName: 'Export Visual Chronology',
    },
    SearchVisual: {
      description: 'Search visual chronology',
      tracking: true,
      legacyTrackingName: 'Search Visual Chronology',
    },
    SwitchToVisual: {
      description: 'Switch to visual chronology view',
      tracking: true,
      legacyTrackingName: 'Clicked to Switch to Visual Chronology',
    },
  });

const docChronology = resource('docChronology', 'Document Chronologies')
  .childOf(subset)
  .addAction({
    Create: {
      description:
        'Create a document chronology, and edit or delete document chronologies you have created',
      permission: true,
      audit: true,
      tracking: true,
      legacyTrackingName: 'Create document chronology',
    },
    Edit: {
      description: 'Edit any document chronology',
      permission: true,
      audit: true,
    },
    UpdateDoc: {
      description: 'Update a document within a document chronology',
      audit: true,
    },
    AddFact: {
      description: 'Add a fact to a doc chronology document',
      audit: true,
    },
    RemoveFact: {
      description: 'Remove a fact from a doc chronology document',
      audit: true,
    },
    AddFactToChronology: {
      description: 'Add a fact to a doc chronology',
      audit: true,
    },
    AddDocument: {
      description: 'Add a document to a document chronology',
      audit: true,
    },
    RemoveDocument: {
      description: 'Remove a document from a document chronology',
      audit: true,
    },
    Delete: {
      description: 'Delete any document chronology',
      permission: true,
      audit: true,
    },
    AddDocs: {
      description: 'Add docs to document chronology',
      audit: true,
      tracking: true,
      legacyTrackingName: 'Add docs to document chronology',
    },
    Download: {
      description: 'Download any document chronology',
      permission: true,
    },
  });

// CommentAdd is the single permission gate for all comment mutations.
// Update and Delete are audit-only — access is controlled by CommentAdd.
const comment = resource('comment', 'Comments')
  .childOf(subset)
  .addAction({
    Add: {
      description: 'Add, update, and delete comments on facts',
      permission: true,
      audit: true,
    },
    Update: { description: 'Update a comment', audit: true },
    Delete: { description: 'Delete a comment', audit: true },
  });

const thread = resource('thread', 'Kim (AI Assistant)')
  .childOf(subset)
  .addAction({
    Create: {
      description: 'Create threads and ask questions using Kim AI assistant',
      permission: true,
      audit: true,
    },
    Publish: {
      description: 'Publish and manage Kim threads',
      permission: true,
    },
    Delete: { description: 'Delete a Kim thread', audit: true }, // audit-only — gated by ThreadCreate
    AskQuestion: {
      description: 'Ask Kim a question',
      tracking: true,
      legacyTrackingName: 'Kim Question Asked',
    },
    AskFromDocViewer: {
      description: 'Ask Kim from document viewer',
      tracking: true,
      legacyTrackingName: 'Ask Kim From Doc Viewer',
    },
    AskFromMatterOverview: {
      description: 'Ask Kim from Matter overview',
      tracking: true,
      legacyTrackingName: 'Ask Kim From Matter Overview',
    },
    AskFromSubsetSummary: {
      description: 'Ask Kim from subset summary',
      tracking: true,
      legacyTrackingName: 'Ask Kim From Subset Summary',
    },
    AskPresetFromSummary: {
      description: 'Ask Kim preset question from subset summary',
      tracking: true,
      legacyTrackingName: 'Ask Kim Preset Question From Subset Summary',
    },
    ExecutePlan: {
      description: 'Execute Kim plan',
      tracking: true,
      legacyTrackingName: 'Kim Execute Plan',
    },
    ReferenceClicked: {
      description: 'Click Kim reference',
      tracking: true,
      legacyTrackingName: 'Kim Reference Clicked',
    },
    Download: {
      description: 'Download a Kim answer',
      permission: true,
      tracking: true,
      legacyTrackingName: 'Download Kim Answer',
    },
    CopyAnswer: {
      description: 'Copy Kim answer',
      tracking: true,
      legacyTrackingName: 'Copy Kim Answer',
    },
    CopyPageLink: {
      description: 'Copy Kim page link',
      tracking: true,
      legacyTrackingName: 'Copy Kim Page Link',
    },
    ConfidenceLevel: {
      description: 'Click Kim confidence level',
      tracking: true,
      legacyTrackingName: 'Kim Confidence Level Clicked',
    },
    ExampleQuestion: {
      description: 'Click Kim example question',
      tracking: true,
      legacyTrackingName: 'Kim Example Question Clicked',
    },
    FollowUpQuestion: {
      description: 'Click Kim follow up question',
      tracking: true,
      legacyTrackingName: 'Kim Follow Up Question Clicked',
    },
    TagDocument: {
      description: 'Tag a document in Kim with @',
      tracking: true,
      legacyTrackingName: 'Kim @ Document',
    },
    ClickHelp: {
      description: 'Click Kim help',
      tracking: true,
      legacyTrackingName: 'Kim Click Help',
    },
    OnboardingQuestionFlow: {
      description: 'Click question flow in Kim onboarding',
      tracking: true,
      legacyTrackingName: 'Kim Onboarding Click Question Flow',
    },
    OnboardingExtractionFlow: {
      description: 'Click extraction flow in Kim onboarding',
      tracking: true,
      legacyTrackingName: 'Kim Onboarding Click Extraction Flow',
    },
    OnboardingDraftFlow: {
      description: 'Click draft flow in Kim onboarding',
      tracking: true,
      legacyTrackingName: 'Kim Onboarding Click Draft Flow',
    },
    OnboardingExampleQuestion: {
      description: 'Click example question in Kim onboarding',
      tracking: true,
      legacyTrackingName: 'Kim Onboarding Click Example Question',
    },
    OnboardingCustomQuestion: {
      description: 'Click custom question in Kim onboarding',
      tracking: true,
      legacyTrackingName: 'Kim Onboarding Click Custom Question',
    },
    ThreadPublished: {
      description: 'Publish a Kim thread',
      tracking: true,
      legacyTrackingName: 'Kim Thread Published',
    },
    ToggleThinking: {
      description: 'Toggle Kim thinking display',
      tracking: true,
      legacyTrackingName: 'Kim Toggle Thinking',
    },
    UpdatePlan: {
      description: 'Update Kim plan',
      tracking: true,
      legacyTrackingName: 'Kim Update Plan',
    },
  });

const kim = resource('kim', 'Kim (New)')
  .childOf(subset)
  .addAction({
    CreateThread: {
      description: 'Create a new Kim thread',
      tracking: true,
      audit: true,
    },
    SendMessage: {
      description: 'Send a message in Kim',
      tracking: true,
    },
    RenameThread: {
      description: 'Rename a Kim thread',
      tracking: true,
    },
    DeleteThread: {
      description: 'Delete a Kim thread',
      tracking: true,
      audit: true,
    },
    DownloadArtefact: {
      description: 'Download a Kim artefact',
      tracking: true,
    },
    DownloadAnswer: {
      description: 'Download a Kim answer',
      tracking: true,
    },
    EnableNewExperience: {
      description: 'Enable the new Kim experience',
      tracking: true,
    },
    ReturnToOldExperience: {
      description: 'Return to the old Kim experience',
      tracking: true,
    },
    DismissNewKimDialog: {
      description: 'Dismiss the new Kim dialog',
      tracking: true,
    },
  });

// RunCreate is audit-only — any user on a matter can start a run.
// Only approval requires an explicit permission.
const run = resource('run', 'Processing Runs')
  .childOf(matter)
  .addAction({
    Create: { description: 'Start a processing run', audit: true },
    Approve: {
      description: 'Admin approval for processing runs',
      permission: true,
    },
  });

const realtime = resource('realtime', 'Realtime')
  .childOf(matter)
  .addAction({
    Use: {
      description: 'Access Wexler Real Time for live transcription & inconsistency detection',
      permission: true,
      audit: true,
    },
    StartRecording: {
      description: 'Start a recording',
      tracking: true,
      legacyTrackingName: 'Start Recording',
    },
    PauseRecording: {
      description: 'Pause a recording',
      tracking: true,
      legacyTrackingName: 'Pause Recording',
    },
    StopRecording: {
      description: 'Stop a recording',
      tracking: true,
      legacyTrackingName: 'Stop Recording',
    },
    ClickInfo: {
      description: 'Click transcript info button',
      tracking: true,
      legacyTrackingName: 'Click Transcript Info Button',
    },
    EditBlockText: {
      description: 'Edit transcript block text',
      tracking: true,
      legacyTrackingName: 'Edit Transcript Block Text',
    },
    EditBlockSpeaker: {
      description: 'Edit transcript block speaker',
      tracking: true,
      legacyTrackingName: 'Edit Transcript Block Speaker',
    },
    AssignSpeaker: {
      description: 'Assign speaker in transcript',
      tracking: true,
      legacyTrackingName: 'Assign Speaker',
    },
    ViewSummary: {
      description: 'View transcript summary',
      tracking: true,
      legacyTrackingName: 'View Transcript Summary',
    },
    ClickInconsistencySeeMore: {
      description: 'Click inconsistency see more',
      tracking: true,
      legacyTrackingName: 'Click Inconsistency See More',
    },
    ClickInconsistencyReference: {
      description: 'Click inconsistency reference',
      tracking: true,
      legacyTrackingName: 'Click Inconsistency Reference',
    },
    ClickInconsistencyReferenceBlock: {
      description: 'Click inconsistency reference block',
      tracking: true,
      legacyTrackingName: 'Click Inconsistency Reference Block',
    },
    ClickDiarizationCorrection: {
      description: 'Click diarization correction',
      tracking: true,
      legacyTrackingName: 'Click Diarization Correction',
    },
    AcceptDiarizationCorrection: {
      description: 'Accept diarization correction',
      tracking: true,
      legacyTrackingName: 'Accept Diarization Correction',
    },
    RejectDiarizationCorrection: {
      description: 'Reject diarization correction',
      tracking: true,
      legacyTrackingName: 'Reject Diarization Correction',
    },
    ClickNewRecording: {
      description: 'Click new recording button',
      tracking: true,
      legacyTrackingName: 'Click New Recording Button',
    },
    DeleteTranscript: {
      description: 'Delete a transcript',
      tracking: true,
      legacyTrackingName: 'Delete Transcript',
    },
  });

// Kept for audit trail — old events reference this resource and must still render on the frontend.
const inconsistencyReport = resource('inconsistencyReport', 'Inconsistency Reports')
  .childOf(subset)
  .addAction({
    Create: {
      description: 'Create an inconsistency report',
      audit: true,
    },
    Delete: {
      description: 'Delete an inconsistency report',
      audit: true,
    },
  });

const user = resource('user', 'Users')
  .childOf(root)
  .addAction({
    Manage: {
      description: 'Update and assign roles to users, and manage their Matter access',
      permission: true,
    },
    ClickAddWhileProcessing: {
      description: 'Click add users while processing',
      tracking: true,
      legacyTrackingName: 'Click Add Users While Processing',
    },
  });

const integrations = resource('integrations', 'Integrations')
  .childOf(root)
  .addAction({
    Manage: {
      description: 'Configure and manage external system integrations',
      permission: true,
    },
    UploadAttempted: {
      description: 'Integration upload attempted',
      tracking: true,
      legacyTrackingName: 'Integration Upload Attempted',
    },
    UploadSuccessful: {
      description: 'Integration upload successful',
      tracking: true,
      legacyTrackingName: 'Integration Upload Successful',
    },
    UploadUnsuccessful: {
      description: 'Integration upload unsuccessful',
      tracking: true,
      legacyTrackingName: 'Integration Upload Unsuccessful',
    },
    Selected: {
      description: 'Integration selected',
      tracking: true,
      legacyTrackingName: 'Integration Selected',
    },
  });

const tokenLimits = resource('token-limits', 'Credit Approval Limits')
  .childOf(root)
  .addAction({
    Manage: {
      description: 'Manage credit approval limits',
      permission: true,
    },
  });

const relativityNode = resource('relativity', 'Relativity')
  .childOf(root)
  .addAction({
    Import: {
      description: 'Import documents and data from Relativity',
      permission: true,
    },
  });

const usageDashboard = resource('usage-dashboard', 'Usage Dashboard')
  .childOf(root)
  .addAction({
    View: {
      description: 'Access organisation usage analytics and reports',
      permission: true,
    },
    DownloadData: {
      description: 'Download usage data',
      tracking: true,
      legacyTrackingName: 'Download Usage Data',
    },
  });

const audit = resource('audit', 'Audit')
  .childOf(root)
  .addAction({
    View: {
      description: 'Access detailed audit trails of accessible Matters',
      permission: true,
    },
  });

const docViewer = resource('doc-viewer', 'Document Viewer')
  .childOf(root)
  .addAction({
    OpenFromFact: {
      description: 'Open document viewer from a fact',
      tracking: true,
      legacyTrackingName: 'Open Doc Viewer From Fact',
    },
    OpenFromKim: {
      description: 'Open document viewer from Kim',
      tracking: true,
      legacyTrackingName: 'Open Doc Viewer From Kim',
    },
    OpenFromChronology: {
      description: 'Open document viewer from chronology',
      tracking: true,
      legacyTrackingName: 'Open Doc Viewer From Chronology',
    },
    OpenFromVisualChronology: {
      description: 'Open document viewer from visual chronology',
      tracking: true,
      legacyTrackingName: 'Open Doc Viewer From Visual Chronology',
    },
    ShowAllFacts: {
      description: 'Show all facts in document viewer',
      tracking: true,
      legacyTrackingName: 'Show All Facts In Doc Viewer',
    },
    DownloadDocument: {
      description: 'Download document in document viewer',
      tracking: true,
      legacyTrackingName: 'Download Document In Doc Viewer',
    },
    JumpToFact: {
      description: 'Jump to fact in document from doc facts list',
      tracking: true,
      legacyTrackingName: 'Jump To Fact In Doc From Doc Facts List',
    },
  });

const navigation = resource('navigation', 'Navigation')
  .childOf(root)
  .addAction({
    ClickMatterCard: {
      description: 'Click Matter card on homepage',
      tracking: true,
      legacyTrackingName: 'Click Matter Card',
    },
    ClickRecentSubset: {
      description: 'Click recent subset on homepage',
      tracking: true,
      legacyTrackingName: 'Click Recent Subset',
    },
  });

const onboarding = resource('onboarding', 'Onboarding')
  .childOf(root)
  .addAction({
    ClickUpload: {
      description: 'Click upload documents in onboarding',
      tracking: true,
      legacyTrackingName: 'Onboarding Click Upload Documents',
    },
    ClickCreateMatter: {
      description: 'Click create Matter in onboarding',
      tracking: true,
      legacyTrackingName: 'Onboarding Click Create Matter',
    },
    ClickCreateSubset: {
      description: 'Click create subset in onboarding',
      tracking: true,
      legacyTrackingName: 'Onboarding Click Create Subset',
    },
  });

const fileHistory = resource('file-history', 'File History')
  .childOf(root)
  .addAction({
    TabChanged: {
      description: 'File history tab changed',
      tracking: true,
      legacyTrackingName: 'File History Tab Changed',
    },
    DownloadOverviewReport: {
      description: 'Download file history overview report',
      tracking: true,
      legacyTrackingName: 'File History Download Overview Report',
    },
    ViewSkippedBreakdown: {
      description: 'View skipped files breakdown',
      tracking: true,
      legacyTrackingName: 'File History View Skipped Breakdown',
    },
    ViewDeletedBreakdown: {
      description: 'View deleted files breakdown',
      tracking: true,
      legacyTrackingName: 'File History View Deleted Breakdown',
    },
    ClickUploadEvent: {
      description: 'Click upload event in file history',
      tracking: true,
      legacyTrackingName: 'File History Click Upload Event',
    },
    ClickDeletionEvent: {
      description: 'Click deletion event in file history',
      tracking: true,
      legacyTrackingName: 'File History Click Deletion Event',
    },
    OpenSkippedFilesModal: {
      description: 'Open skipped files modal',
      tracking: true,
      legacyTrackingName: 'File History Open Skipped Files Modal',
    },
    OpenDeletedFilesModal: {
      description: 'Open deleted files modal',
      tracking: true,
      legacyTrackingName: 'File History Open Deleted Files Modal',
    },
    DownloadSkippedReport: {
      description: 'Download skipped files report',
      tracking: true,
      legacyTrackingName: 'File History Download Skipped Report',
    },
    DownloadDeletedReport: {
      description: 'Download deleted files report',
      tracking: true,
      legacyTrackingName: 'File History Download Deleted Report',
    },
    OpenSkippedExplainer: {
      description: 'Open skipped files explainer',
      tracking: true,
      legacyTrackingName: 'File History Open Skipped Explainer',
    },
    OpenUploadExplainer: {
      description: 'Open upload explainer',
      tracking: true,
      legacyTrackingName: 'File History Open Upload Explainer',
    },
    DownloadUploadBatchSkippedReport: {
      description: 'Download upload batch skipped report',
      tracking: true,
      legacyTrackingName: 'File History Download Upload Batch Skipped Report',
    },
    DownloadDeletionBatchReport: {
      description: 'Download deletion detail report',
      tracking: true,
      legacyTrackingName: 'File History Download Deletion Detail Report',
    },
    ClickFailedPages: {
      description: 'Click failed pages in file history',
      tracking: true,
      legacyTrackingName: 'File History Click Failed Pages',
    },
    OpenBreakdownLink: {
      description: 'Open breakdown link in file history',
      tracking: true,
      legacyTrackingName: 'File History Open Breakdown Link',
    },
    OpenBreakdownModal: {
      description: 'Open breakdown modal in file history',
      tracking: true,
      legacyTrackingName: 'File History Open Breakdown Modal',
    },
  });

const pwa = resource('pwa', 'PWA')
  .childOf(root)
  .addAction({
    PromptInstall: {
      description: 'PWA install prompt clicked',
      tracking: true,
      legacyTrackingName: 'PWA Prompt Install Clicked',
    },
    PromptDismissed: {
      description: 'PWA install prompt dismissed',
      tracking: true,
      legacyTrackingName: 'PWA Prompt Dismissed',
    },
  });

const inconsistency = resource('inconsistency', 'Inconsistencies')
  .childOf(matter)
  .addAction({
    Delete: {
      description: 'Delete an inconsistency',
      permission: true,
      audit: true,
      tracking: true,
    },
    Download: {
      description: 'Download inconsistencies',
      permission: true,
      tracking: true,
    },
    Search: {
      description: 'Search inconsistencies',
      tracking: true,
      legacyTrackingName: 'Search Inconsistencies',
    },
    FilterByDocument: {
      description: 'Filter inconsistencies by document',
      tracking: true,
      legacyTrackingName: 'Filter Inconsistencies By Document',
    },
    FilterBySeverity: {
      description: 'Filter inconsistencies by severity',
      tracking: true,
      legacyTrackingName: 'Filter Inconsistencies By Severity',
    },
    FilterBySource: {
      description: 'Filter inconsistencies by source',
      tracking: true,
      legacyTrackingName: 'Filter Inconsistencies By Source',
    },
    FilterByType: {
      description: 'Filter inconsistencies by type',
      tracking: true,
      legacyTrackingName: 'Filter Inconsistencies By Type',
    },
    OpenViewer: {
      description: 'Open inconsistency viewer',
      tracking: true,
      legacyTrackingName: 'Open Inconsistency Viewer',
    },
  });

const person = resource('person', 'Entities')
  .childOf(matter)
  .addAction({
    FilterByType: {
      description: 'Filter person by type',
      tracking: true,
      legacyTrackingName: 'Filter Person By Type',
    },
    ClickKeyFromHome: {
      description: 'Click key entity from home',
      tracking: true,
      legacyTrackingName: 'Click Key Entity From Home',
    },
    ClickTab: {
      description: 'Click entities tab',
      tracking: true,
      legacyTrackingName: 'Click Entities Tab',
    },
    ViewAllOnSummary: {
      description: 'View all entities on summary',
      tracking: true,
      legacyTrackingName: 'View All Entities On Summary',
    },
    Download: {
      description: 'Download entities',
      permission: true,
    },
  });

const nlSearch = resource('nl-search', 'Natural Language Search')
  .childOf(root)
  .addAction({
    ViaWand: {
      description: 'Use natural language search via wand button',
      tracking: true,
      legacyTrackingName: 'Use natural language search via wand button',
    },
    ViaSpace: {
      description: 'Use natural language search via space key pressed',
      tracking: true,
      legacyTrackingName: 'Use natural language search via space key pressed',
    },
  });

const clientMaterials = resource('client-materials', 'Client Materials')
  .childOf(root)
  .addAction({
    CopyEmailTemplate: {
      description: 'Copy email template',
      tracking: true,
      legacyTrackingName: 'Copy Email Template',
    },
    GenerateCaseDetails: {
      description: 'Generate case details',
      tracking: true,
      legacyTrackingName: 'Generate Case Details',
    },
  });

// ===== Build registry =====

const registry = buildRegistry(
  root,
  matter,
  document,
  subset,
  fact,
  chronology,
  docChronology,
  comment,
  thread,
  kim,
  run,
  realtime,
  inconsistencyReport,
  user,
  integrations,
  tokenLimits,
  relativityNode,
  usageDashboard,
  audit,
  docViewer,
  navigation,
  onboarding,
  fileHistory,
  pwa,
  inconsistency,
  person,
  nlSearch,
  clientMaterials,
);

// ===== Typed exports =====

export const REGISTRY = registry;

export const Permission = registry.permissions;
export type Permission = (typeof Permission)[keyof typeof Permission];

export const TrackingEvent = registry.trackingEvents;
export type TrackingEvent = (typeof TrackingEvent)[keyof typeof TrackingEvent];

export const AuditEventType = registry.auditEvents;
export type AuditEventType = (typeof AuditEventType)[keyof typeof AuditEventType];
